/**
 * KSA policy tables — versioned DATA, not scattered constants (ADR 0052).
 *
 * Every threshold the policy engine computes over lives here, with its legal
 * basis, so a law change is a single reviewable diff (table + tests) and every
 * verdict can cite its rule. Values reflect the Saudi Labour Law as commonly
 * applied.
 *
 * ⚠️ PENDING HR-OWNER REVIEW (plan §6): the People Engine is a simulation and
 * every v1 playbook ends in a human gate, but these numbers must be verified
 * by the HR owner before anything real depends on them. Update `reviewed`
 * when that pass happens; revisions bump `version` alongside the old values
 * in git history.
 */

export interface SickLeaveTier {
  /** Length of this tier in days. */
  readonly days: number;
  /** Pay rate during the tier (1 = full wage). */
  readonly payRate: number;
}

export interface ResignationRule {
  /** Below this service length a resignation earns nothing. */
  readonly thirdFromYears: number;
  /** Through this service length (inclusive) the fraction is one third. */
  readonly thirdToYearsInclusive: number;
  /** From this service length the full award is earned. */
  readonly fullFromYears: number;
  readonly fractions: { readonly third: number; readonly twoThirds: number };
}

export interface PublicHolidayRule {
  readonly name: string;
  /** Which calendar the anchor date lives in. */
  readonly calendar: 'gregorian' | 'hijri';
  /** 1-based month in that calendar. */
  readonly month: number;
  /** 1-based first day of the holiday block. */
  readonly day: number;
  readonly lengthDays: number;
  readonly ruleRef: string;
}

export const KSA_POLICY = {
  version: '2026-07',
  reviewed: 'pending HR-owner review (PEOPLE-3)',

  annualLeave: {
    baseDays: 21,
    longServiceDays: 30,
    longServiceAfterYears: 5,
    ruleRef: 'Saudi Labour Law art. 109',
  },

  sickLeave: {
    /** Per service year: 30 days full pay → 60 at 75% → 30 unpaid. */
    tiers: [
      { days: 30, payRate: 1 },
      { days: 60, payRate: 0.75 },
      { days: 30, payRate: 0 },
    ] as readonly SickLeaveTier[],
    ruleRef: 'Saudi Labour Law art. 117',
  },

  eosb: {
    /** Half-month wage per year for the first five years, then a full month. */
    firstYears: 5,
    firstYearsRate: 0.5,
    laterYearsRate: 1,
    ruleRefAward: 'Saudi Labour Law art. 84',
    /**
     * Resignation fractions (art. 85): under 2 years nothing; 2–5 years
     * inclusive one third; over 5 and under 10 two thirds; 10+ the full award.
     */
    resignation: {
      thirdFromYears: 2,
      thirdToYearsInclusive: 5,
      fullFromYears: 10,
      fractions: { third: 1 / 3, twoThirds: 2 / 3 },
    } as ResignationRule,
    ruleRefResignation: 'Saudi Labour Law art. 85',
  },

  probation: {
    maxDays: 90,
    extendedMaxDays: 180,
    ruleRef: 'Saudi Labour Law art. 53',
  },

  workweek: {
    /** UTC `getUTCDay()` values that are the weekend: Friday (5), Saturday (6). */
    weekendDays: [5, 6] as readonly number[],
    standardDailyHours: 8,
    /** Reduced working hours for Muslim employees during Ramadan. */
    ramadanDailyHours: 6,
    ruleRef: 'Saudi Labour Law arts. 98 / 101',
  },

  overtime: {
    /** Overtime is paid at 150% of the basic hourly wage. */
    rate: 1.5,
    ruleRef: 'Saudi Labour Law art. 107',
  },

  publicHolidays: [
    {
      name: 'Eid al-Fitr',
      calendar: 'hijri',
      month: 10, // Shawwal
      day: 1,
      lengthDays: 4,
      ruleRef: 'Saudi Labour Law art. 112 + implementing regulations',
    },
    {
      name: 'Eid al-Adha',
      calendar: 'hijri',
      month: 12, // Dhu al-Hijjah, from the day of Arafah
      day: 9,
      lengthDays: 4,
      ruleRef: 'Saudi Labour Law art. 112 + implementing regulations',
    },
    {
      name: 'Saudi National Day',
      calendar: 'gregorian',
      month: 9,
      day: 23,
      lengthDays: 1,
      ruleRef: 'Saudi Labour Law art. 112',
    },
    {
      name: 'Founding Day',
      calendar: 'gregorian',
      month: 2,
      day: 22,
      lengthDays: 1,
      ruleRef: 'Royal Order A/197 (2022)',
    },
  ] as readonly PublicHolidayRule[],
} as const;
